import React, { useEffect, useState } from "react";

import {
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import Header from "../components/layout/Header";
import Sidebar from "../components/layout/Sidebar";
import FloatingAIButton from "../components/layout/FloatingAIButton";

/* =========================================================
   FIRM PAGES
========================================================= */

import Dashboard from "../pages/Dashboard";
import Clients from "../pages/Clients";
import Invoices from "../pages/Invoices";
import CreateInvoice from "../pages/invoices/CreateInvoice";

import Bills from "../pages/Bills";
import Customers from "../pages/Customers";
import Vendors from "../pages/Vendors";
import Transactions from "../pages/Transactions";
import Receipts from "../pages/Receipts";
import RecurringInvoices from "../pages/RecurringInvoices";
import PaymentsReceived from "../pages/PaymentsReceived";
import PaymentsMade from "../pages/PaymentsMade";

/* =========================================================
   ACCOUNTING
========================================================= */

import GeneralLedger from "../pages/accounting/GeneralLedger";
import Reconciliation from "../pages/accounting/Reconciliation";

import ChartOfAccounts from "../pages/ChartOfAccounts";
import ConnectBank from "../pages/ConnectBank";

/* =========================================================
   SETTINGS
========================================================= */

import CompanySetup from "../pages/settings/CompanySetup";
import Billing from "../pages/settings/Billing";
import Payments from "../pages/settings/Payments";
import Integrations from "../pages/settings/Integrations";
import UserProfile from "../pages/settings/UserProfile";

/* =========================================================
   REPORTS
========================================================= */

import ProfitLossReport from "../pages/reports/ProfitLossReport";
import BalanceSheetReport from "../pages/reports/BalanceSheetReport";
import CashFlowReport from "../pages/reports/CashFlowReport";
import ARAgingReport from "../pages/reports/ARAgingReport";
import APAgingReport from "../pages/reports/APAgingReport";

import CustomerStatements from "../pages/CustomerStatements";
import VendorCredits from "../pages/purchases/VendorCredits";
import BankRules from "../pages/BankRules";

/* =========================================================
   AI AND OTHER PAGES
========================================================= */

import Assistant from "../pages/Assistant";
import AuditLog from "../pages/AuditLog";

/* =========================================================
   FIRM LAYOUT

   Existing Header.jsx is preserved.

   This layout is used by accounting firms
   and accountants, not clients.
========================================================= */

export default function FirmLayout() {
  const location = useLocation();

  const [isSidebarOpen, setIsSidebarOpen] =
    useState(false);

  const [
    isSidebarCollapsed,
    setIsSidebarCollapsed,
  ] = useState(false);

  const isAssistantPage =
    location.pathname === "/" ||
    location.pathname === "/assistant";

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex h-screen min-h-0 w-full overflow-hidden bg-[#F8FAFC]">
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        setIsCollapsed={setIsSidebarCollapsed}
      />

      <div
        className={`flex h-full min-h-0 min-w-0 flex-1 flex-col transition-all duration-300 ${
          isSidebarCollapsed
            ? "md:ml-20"
            : "md:ml-72"
        }`}
      >
        <Header
          onMenuClick={() => setIsSidebarOpen(true)}
        />

        <main
          className={
            isAssistantPage
              ? "min-h-0 flex-1 overflow-hidden"
              : "min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-8"
          }
        >
          <Routes>
            <Route
              path="/"
              element={<Assistant />}
            />

            <Route
              path="/assistant"
              element={<Assistant />}
            />

            <Route
              path="/dashboard"
              element={<Dashboard />}
            />

            <Route
              path="/clients"
              element={<Clients />}
            />

            <Route
              path="/audit-log"
              element={<AuditLog />}
            />

            <Route
              path="/sales/invoices"
              element={<Invoices />}
            />

            <Route
              path="/sales/invoices/new"
              element={<CreateInvoice />}
            />

            <Route
              path="/sales/payments-received"
              element={<PaymentsReceived />}
            />

            <Route
              path="/sales/recurring-invoices"
              element={<RecurringInvoices />}
            />

            <Route
              path="/sales/customers"
              element={<Customers />}
            />

            <Route
              path="/sales/statements"
              element={<CustomerStatements />}
            />

            <Route
              path="/purchases/vendors"
              element={<Vendors />}
            />

            <Route
              path="/purchases/bills"
              element={<Bills />}
            />

            <Route
              path="/purchases/payments-made"
              element={<PaymentsMade />}
            />

            <Route
              path="/purchases/vendor-credits"
              element={<VendorCredits />}
            />

            <Route
              path="/receipts"
              element={<Receipts />}
            />

            <Route
              path="/accounting/connect-bank"
              element={<ConnectBank />}
            />

            <Route
              path="/accounting/transactions"
              element={<Transactions />}
            />

            <Route
              path="/accounting/reconciliation"
              element={<Reconciliation />}
            />

            <Route
              path="/accounting/rules"
              element={<BankRules />}
            />

            <Route
              path="/accounting/chart-of-accounts"
              element={<ChartOfAccounts />}
            />

            <Route
              path="/accounting/general-ledger"
              element={
                <Navigate
                  to="/reports/transaction-details"
                  replace
                />
              }
            />

            <Route
              path="/reports/standard"
              element={
                <Navigate
                  to="/reports/profit-loss"
                  replace
                />
              }
            />

            <Route
              path="/reports/transaction-details"
              element={<GeneralLedger />}
            />

            <Route
              path="/reports/profit-loss"
              element={<ProfitLossReport />}
            />

            <Route
              path="/reports/balance-sheet"
              element={<BalanceSheetReport />}
            />

            <Route
              path="/reports/trial-balance"
              element={
                <Navigate
                  to="/reports/profit-loss"
                  replace
                />
              }
            />

            <Route
              path="/reports/cash-flow"
              element={<CashFlowReport />}
            />

            <Route
              path="/reports/ar-aging"
              element={<ARAgingReport />}
            />

            <Route
              path="/reports/ap-aging"
              element={<APAgingReport />}
            />

            <Route
              path="/settings/company"
              element={<CompanySetup />}
            />

            <Route
              path="/settings/billing"
              element={<Billing />}
            />

            <Route
              path="/settings/payments"
              element={<Payments />}
            />

            <Route
              path="/settings/integrations"
              element={<Integrations />}
            />

            <Route
              path="/settings/profile"
              element={<UserProfile />}
            />

            <Route
              path="*"
              element={
                <Navigate
                  to="/"
                  replace
                />
              }
            />
          </Routes>
        </main>

        {!isAssistantPage && <FloatingAIButton />}
      </div>
    </div>
  );
}